import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { GameDto, MoveResultDto, Position } from './types.js';
import { authService } from './auth.js';

export interface GameUpdateCallbacks {
  onMoveReceived?: (gameId: string, move: Position | undefined, result: MoveResultDto) => void;
  onPlayerJoined?: (gameId: string, player: any) => void;
  onPlayerLeft?: (gameId: string, playerId: string) => void;
  onGameStateChanged?: (gameId: string, game: GameDto) => void;
  onConnectionChanged?: (connected: boolean) => void;
}

export class SignalRService {
  private connection: HubConnection | null = null;
  private callbacks: GameUpdateCallbacks = {};
  private isConnecting: boolean = false;
  private currentGameId: string | null = null;

  constructor() {
    this.initializeConnection();
  }

  private initializeConnection() {
    // Use same logic as API URL
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const hubUrl = isLocalhost
      ? 'http://localhost:5036/gameHub'
      : '/gameHub';
    
    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect([0, 2000, 10000, 30000]) // Reconnect delays
      .build();

    // Set up event handlers
    this.setupEventHandlers();
  }

  private setupEventHandlers() {
    if (!this.connection) return;

    // Connection state events
    this.connection.onreconnecting(() => {
      console.log('SignalR: Reconnecting...');
      this.callbacks.onConnectionChanged?.(false);
    });

    this.connection.onreconnected(() => {
      console.log('SignalR: Reconnected!');
      this.callbacks.onConnectionChanged?.(true);
      
      // Rejoin current game if we have one
      if (this.currentGameId) {
        this.joinGameGroup(this.currentGameId);
      }
    });

    this.connection.onclose(() => {
      console.log('SignalR: Connection closed');
      this.callbacks.onConnectionChanged?.(false);
    });

    // Game event handlers
    this.connection.on('MoveMade', (data: any) => {
      console.log('SignalR: Move made', data);
      // Use the current game ID since the event doesn't include it
      const gameId = this.currentGameId;
      if (gameId) {
        // Convert move DTO to Position (toX is row, toY is column)
        const position = data.move ? { row: data.move.toX, col: data.move.toY } : undefined;
        this.callbacks.onMoveReceived?.(gameId, position, data);
      }
    });

    this.connection.on('PlayerJoined', (gameId: string, player: any) => {
      console.log('SignalR: Player joined', { gameId, player });
      this.callbacks.onPlayerJoined?.(gameId, player);
    });

    this.connection.on('PlayerLeft', (gameId: string, playerId: string) => {
      console.log('SignalR: Player left', { gameId, playerId });
      this.callbacks.onPlayerLeft?.(gameId, playerId);
    });

    this.connection.on('GameJoined', (game: GameDto) => {
      console.log('SignalR: Game joined', game);
      this.callbacks.onGameStateChanged?.(game.id, game);
    });

    // Error handling
    this.connection.on('Error', (message: string) => {
      console.error('SignalR Error:', message);
    });
  }

  async connect(accessToken?: string): Promise<boolean> {
    if (this.isConnecting) {
      return false;
    }

    if (this.connection?.state === HubConnectionState.Connected) {
      return true;
    }

    this.isConnecting = true;

    try {
      // Use same logic as API URL
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const hubUrl = isLocalhost
        ? 'http://localhost:5036/gameHub'
        : '/gameHub';
      
      // Use Keycloak token if available, otherwise use provided token
      const token = accessToken || authService.getToken();
      
      // Always rebuild connection with or without token
      if (token) {
        this.connection = new HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token
          })
          .withAutomaticReconnect([0, 2000, 10000, 30000])
          .build();
      } else {
        this.connection = new HubConnectionBuilder()
          .withUrl(hubUrl)
          .withAutomaticReconnect([0, 2000, 10000, 30000])
          .build();
      }
      
      this.setupEventHandlers();
      await this.connection.start();
      console.log('SignalR: Connected successfully');
      this.callbacks.onConnectionChanged?.(true);
      return true;
    } catch (error) {
      console.error('SignalR: Failed to connect', error);
      this.callbacks.onConnectionChanged?.(false);
      return false;
    } finally {
      this.isConnecting = false;
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('SignalR: Disconnected');
      } catch (error) {
        console.error('SignalR: Error during disconnect', error);
      }
    }
  }

  async joinGameGroup(gameId: string): Promise<boolean> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      console.warn('SignalR: Cannot join game group - not connected');
      return false;
    }

    try {
      await this.connection.invoke('JoinGame', gameId);
      this.currentGameId = gameId;
      console.log(`SignalR: Joined game group ${gameId}`);
      return true;
    } catch (error) {
      console.error('SignalR: Failed to join game group', error);
      return false;
    }
  }

  async leaveGameGroup(gameId: string): Promise<boolean> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      return false;
    }

    try {
      await this.connection.invoke('LeaveGame', gameId);
      if (this.currentGameId === gameId) {
        this.currentGameId = null;
      }
      console.log(`SignalR: Left game group ${gameId}`);
      return true;
    } catch (error) {
      console.error('SignalR: Failed to leave game group', error);
      return false;
    }
  }

  setCallbacks(callbacks: GameUpdateCallbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  getCurrentGameId(): string | null {
    return this.currentGameId;
  }

  getConnectionState(): string {
    if (!this.connection) return 'Disconnected';
    
    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return 'Connected';
      case HubConnectionState.Connecting:
        return 'Connecting';
      case HubConnectionState.Reconnecting:
        return 'Reconnecting';
      case HubConnectionState.Disconnected:
        return 'Disconnected';
      case HubConnectionState.Disconnecting:
        return 'Disconnecting';
      default:
        return 'Unknown';
    }
  }
}